(() => {
  /* Hero window: 1:42 to 1:53 of the Washoe County 100-body install.
     1:40-1:48 is the finished walk-in - a corridor of tiered racks, AMC-branded trays, evaporators overhead.
     1:48-1:53 pulls out to the completed facility from the air.
     That stretch is the only run of finished-product footage with no burned-in bullet overlays: the
     "5 YEAR COMPRESSOR WARRANTY / TIERED STORAGE RACKS" titles run 1:26-1:40 and would fight the hero
     headline, everything before 1:20 is a jobsite, and 1:54 on is the old logo outro card.

     The loop is driven here rather than by YouTube's own end/loop parameters. Those are unreliable: `end`
     does not fire consistently, and `loop` with a playlist restarts the whole video, which is what put the
     old logo animation and the outro card on screen. Instead the clip is polled and seeked back inside the
     window, so playback never reaches the outro, the end screen or the related-video grid.

     STARTUP - the whole point of the current shape. This used to wait for window 'load' and THEN for a
     requestIdleCallback with an 800ms timeout before it even began fetching the YouTube API, on top of the
     API request itself, player construction and buffering. That stack is the "random lapse" at the start:
     several seconds of loading state before any footage appeared, varying with whatever else the page was
     doing. It now preconnects to YouTube's hosts as soon as this file runs and starts the player as soon as
     the hero is in view, with no load or idle gate. Do not reintroduce one: the hero is the first thing on
     the page, so deferring it defers the only content above the fold.

     YouTube also starts a stream at a low rendition and ramps up, which reads as a fuzzy first few seconds
     on a hero this large. hd1080 is requested up front, again when playback starts, and again if the player
     downgrades. The shimmer is held a short beat after playback begins so any YouTube title flash happens
     behind it rather than on screen - 250ms, not the 900ms it was, which was itself part of the delay. */
  const HD = 'hd1080';
  const CLIP_START = 104; /* 1:44 - skips the rooftop and vehicle frames so the loop opens on the cooler and racking */
  const CLIP_END = 113;
  /* Phones open on the vehicles still for 3s, then fade to footage, so the first thing seen is the jobsite arrival shot
     and any YouTube title flash happens behind the still. */
  /* 2026-10-14: 3.2s on every screen. The still holds through the hero's lights-on entrance, and YouTube's
     title bar (shown for the first ~3s of playback) has faded before the footage is revealed. */
  const POSTER_HOLD = 200;
  /* CINEMA PASS (2026-10-13). Each loop is played as a shot: the CSS pushes in slowly across the clip,
     and CUT_LEAD seconds before CLIP_END the frame dips to black, the seek back to CLIP_START happens
     under the black, and the push restarts. See assets/amc-hero-video-cinema.css. */
  const CUT_LEAD = 0.55;
  const CUT_HOLD = 420;
  /* Connection setup to YouTube's hosts is several hundred ms of DNS + TLS that would otherwise happen only
     after the API script request begins. Warming it here overlaps it with the rest of the page load. */
  const warm = () => {
    if (window.amcFacilityWarmed) return;
    window.amcFacilityWarmed = true;
    ['https://www.youtube-nocookie.com', 'https://www.youtube.com', 'https://i.ytimg.com', 'https://rr1---sn-p5qlsnsk.googlevideo.com'].forEach((href, i) => {
      const link = document.createElement('link');
      link.rel = i < 3 ? 'preconnect' : 'dns-prefetch';
      link.href = href;
      link.crossOrigin = '';
      document.head.appendChild(link);
    });
  };
  const loadApi = () => {
    if (window.YT?.Player) return Promise.resolve(window.YT);
    if (window.amcFacilityYouTubeApi) return window.amcFacilityYouTubeApi;
    window.amcFacilityYouTubeApi = new Promise((resolve, reject) => {
      const previousReady = window.onYouTubeIframeAPIReady;
      const timeout = setTimeout(() => reject(new Error('Video player unavailable')), 12000);
      window.onYouTubeIframeAPIReady = () => {
        clearTimeout(timeout);
        try { if (typeof previousReady === 'function') previousReady(); }
        finally { resolve(window.YT); }
      };
      if (!document.querySelector('script[src="https://www.youtube.com/iframe_api"]')) {
        const script = document.createElement('script');
        script.src = 'https://www.youtube.com/iframe_api';
        script.async = true;
        script.onerror = () => { clearTimeout(timeout); reject(new Error('Video player unavailable')); };
        document.head.appendChild(script);
      }
    });
    return window.amcFacilityYouTubeApi;
  };
  document.querySelectorAll('[data-amc-facility-video]').forEach(root => {
    if (root.dataset.initialized) return;
    root.dataset.initialized = 'true';
    const slide = root.closest('[data-amc-hero-slide]');
    const watch = root.querySelector('[data-amc-video-watch]');
    const reduced = matchMedia('(prefers-reduced-motion: reduce)');
    const saveData = navigator.connection?.saveData;
    const desktopMotion = matchMedia('(min-width: 901px)');
    /* Phones play the same muted inline clip; the poster stays up until playback is confirmed. */
    const motionAllowed = () => desktopMotion.matches && !reduced.matches && !saveData;
    let pageReady = document.readyState === 'complete';
    if (!pageReady) {
      addEventListener('load', () => {
        const begin = () => { pageReady = true; sync(); };
        if ('requestIdleCallback' in window) requestIdleCallback(begin, { timeout: 1600 });
        else setTimeout(begin, 200);
      }, { once: true });
    }
    let inView = false;
    let player;
    let ready = false;
    let loading = false;
    let failed = false;
    let requested = false;
    let posterTimer = 0;
    let windowTimer = 0;
    let cutting = false;
    let userPaused = false;
    /* MOBILE NUDGE: phones often ignore the first autoplay request (iOS Low Power Mode, in-app browsers,
       Android data saver). Re-send play a few times after the player is ready, and again on the visitor's
       first touch, scroll or click anywhere on the page. The still stays up until PLAYING is confirmed. */
    const isPlaying = () => { try { return player && player.getPlayerState && player.getPlayerState() === 1; } catch (e) { return false; } };
    const nudge = () => {
      if (!player || failed || userPaused || isPlaying()) return;
      try { player.mute(); player.playVideo(); } catch (e) { /* not ready */ }
    };
    const nudgeTimers = () => { [700, 1800, 3500, 6000, 9000].forEach(ms => setTimeout(nudge, ms)); };
    const onGesture = () => {
      if (!requested && (!pageReady || !motionAllowed())) return;
      if (!player) { start(); return; }
      nudge();
      if (isPlaying()) ['touchstart', 'touchend', 'scroll', 'click', 'keydown'].forEach(t => removeEventListener(t, onGesture, true));
    };
    ['touchstart', 'touchend', 'scroll', 'click', 'keydown'].forEach(t => addEventListener(t, onGesture, {capture: true, passive: true}));
    const restartPush = () => {
      /* Restart the CSS push-in so every loop opens wide again. */
      root.style.animation = 'none';
      void root.offsetWidth;
      root.style.animation = '';
    };
    const cut = () => {
      if (cutting) return;
      cutting = true;
      root.classList.add('is-cut');
      setTimeout(() => {
        rewind();
        restartPush();
        setTimeout(() => { root.classList.remove('is-cut'); cutting = false; }, 160);
      }, CUT_HOLD);
    };
    /* Pause control, bottom right of the hero, shown once footage is playing. */
    const hero = root.closest('.amc-opening-hero') || root.parentElement;
    const pauseBtn = document.createElement('button');
    pauseBtn.type = 'button';
    pauseBtn.className = 'amc-hero-pause';
    pauseBtn.setAttribute('aria-pressed', 'false');
    pauseBtn.setAttribute('aria-label', 'Pause background video');
    pauseBtn.innerHTML = '<svg class="amc-i-pause" viewBox="0 0 14 14" aria-hidden="true"><rect x="2.5" y="1.5" width="3" height="11" rx="1" fill="currentColor"/><rect x="8.5" y="1.5" width="3" height="11" rx="1" fill="currentColor"/></svg><svg class="amc-i-play" viewBox="0 0 14 14" aria-hidden="true"><path d="M3.5 1.8v10.4a.6.6 0 0 0 .9.5l8.2-5.2a.6.6 0 0 0 0-1L4.4 1.3a.6.6 0 0 0-.9.5z" fill="currentColor"/></svg>';
    if (hero) {
      if (getComputedStyle(hero).position === 'static') hero.style.position = 'relative';
      hero.appendChild(pauseBtn);
    }
    /* The next section overlaps the bottom of the hero by a width-dependent amount, so the control is lined
       up with the category row instead of the hero's bottom edge. */
    const placePause = () => {
      if (!desktopMotion.matches && !requested) return;
      const chips = hero && hero.querySelector('.amc-hero-categories');
      if (!chips) return;
      const hb = hero.getBoundingClientRect(), cb = chips.getBoundingClientRect();
      if (!cb.height) return;
      pauseBtn.style.setProperty('--amc-pause-bottom', Math.max(14, Math.round(hb.bottom - cb.bottom + (cb.height - 38) / 2)) + 'px');
    };
    placePause();
    addEventListener('resize', placePause, {passive: true});
    addEventListener('load', placePause, {once: true});
    pauseBtn.addEventListener('click', () => {
      userPaused = !userPaused;
      pauseBtn.setAttribute('aria-pressed', String(userPaused));
      pauseBtn.setAttribute('aria-label', userPaused ? 'Play background video' : 'Pause background video');
      root.classList.toggle('is-user-paused', userPaused);
      sync();
    });
    /* Warm the connection immediately; the player itself still waits for the hero to be in view. */
    /* Background playback must not compete with the first usable page. */
    const forceHd = () => {
      if (!player || typeof player.setPlaybackQuality !== 'function') return;
      try { player.setPlaybackQuality(HD); } catch (error) { /* player not ready yet */ }
    };
    const rewind = () => {
      if (!player || typeof player.seekTo !== 'function') return;
      try { player.seekTo(CLIP_START, true); player.playVideo(); } catch (error) { /* ignore */ }
    };
    /* Keep playback inside the window. Checked often enough that the cut back is not visible as a jump
       into the outro, and cheap enough to leave running. */
    const watchWindow = () => {
      clearInterval(windowTimer);
      windowTimer = setInterval(() => {
        if (!player || typeof player.getCurrentTime !== 'function') return;
        let at;
        try { at = player.getCurrentTime(); } catch (error) { return; }
        if (typeof at !== 'number') return;
        if (at >= CLIP_END || at < CLIP_START - 1) { if (!cutting) { rewind(); restartPush(); } }
        else if (at >= CLIP_END - CUT_LEAD) cut();
      }, 250);
    };
    const canPlay = () => inView && !document.hidden && !userPaused && (!slide || slide.classList.contains('is-active'));
    const sync = () => {
      if (!ready) {
        /* No load gate and no idle gate: if the hero is on screen, start fetching now. */
        if (canPlay() && !loading && !player && !failed && (requested || (pageReady && motionAllowed()))) start();
        return;
      }
      if (canPlay() && (requested || motionAllowed())) { player.playVideo(); forceHd(); }
      else player.pauseVideo();
    };
    const start = () => {
      if (loading || player || failed || !root.isConnected) return;
      loading = true;
      warm();
      loadApi().then(YT => {
        if (!root.isConnected) return;
        player = new YT.Player(root.querySelector('[data-amc-video-player]'), {
          host: 'https://www.youtube-nocookie.com',
          videoId: 'vJ0t4FwOosc',
          width: '100%', height: '100%',
          /* No end, no loop, no playlist: the window is held in script. controls/modestbranding/annotations
             off keep YouTube's own chrome, title card and overlays out of the frame. autoplay:1 so the
             stream begins buffering as the player is constructed rather than after onReady fires. */
          playerVars: {autoplay: 1, mute: 1, controls: 0, disablekb: 1, fs: 0, modestbranding: 1, iv_load_policy: 3, annotations: 0, cc_load_policy: 0, rel: 0, playsinline: 1, vq: HD, start: CLIP_START, origin: location.origin},
          events: {
            onReady: event => {
              ready = true;
              event.target.mute();
              const frame = event.target.getIframe();
              frame.title = 'American Mortuary Coolers 100 body cooler and rack system';
              frame.tabIndex = -1;
              frame.setAttribute('aria-hidden', 'true');
              forceHd();
              watchWindow();
              sync();
              nudgeTimers();
            },
            onStateChange: event => {
              if (event.data === YT.PlayerState.PLAYING) {
                forceHd();
                watchWindow();
                /* Short hold so the first frames, and any YouTube title flash, stay behind the shimmer. */
                if (!posterTimer && !root.classList.contains('is-playing')) {
                  posterTimer = setTimeout(() => { posterTimer = 0; root.classList.add('is-playing'); pauseBtn.classList.add('is-ready'); }, POSTER_HOLD);
                }
              }
              /* Should not be reachable now that the window is held in script, but if the player ever runs
                 to the end, go back into the window rather than letting the end screen paint. */
              if (event.data === YT.PlayerState.ENDED) rewind();
            },
            onPlaybackQualityChange: event => {
              if (event.data !== HD && event.data !== 'highres') forceHd();
            },
            onError: () => { failed = true; clearInterval(windowTimer); root.classList.remove('is-playing'); pauseBtn.classList.remove('is-ready'); },
            onAutoplayBlocked: () => { clearTimeout(posterTimer); posterTimer = 0; root.classList.remove('is-playing'); nudgeTimers(); }
          }
        });
      }).catch(() => { failed = true; root.classList.remove('is-playing'); });
    };
    watch.addEventListener('click', event => {
      if (failed) return;
      event.preventDefault();
      requested = true;
      if (ready) sync(); else start();
    });
    /* rootMargin starts the fetch slightly before the hero scrolls in; on the homepage it is already in
       view at first paint, so this fires immediately. */
    const observer = new IntersectionObserver(entries => {
      inView = entries[0].isIntersecting;
      sync();
    }, {threshold: 0.01, rootMargin: '200px'});
    observer.observe(root);
    if (root.getBoundingClientRect().top < innerHeight) { inView = true; sync(); }
    if (slide) new MutationObserver(sync).observe(slide, {attributes: true, attributeFilter: ['class']});
    document.addEventListener('visibilitychange', sync);
    reduced.addEventListener('change', sync);
    desktopMotion.addEventListener('change', sync);
  });
})();
